import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import LinearProgress from '@material-ui/core/LinearProgress';
import Link from '@material-ui/core/Link';
import { makeStyles } from '@material-ui/core/styles';
import Tab from '@material-ui/core/Tab';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Tabs from '@material-ui/core/Tabs';
import Typography from '@material-ui/core/Typography';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CloseIcon from '@material-ui/icons/Close';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import clsx from 'clsx';
import * as React from 'react';

import Agreement from '../models/Agreement';

export interface Props {
  agreement: Agreement;
  tabIndex: number;
  /**
   * Warning or error message to display as a banner on the popup.
   * @example <IdentifyAgreement message="This agreement has not been mapped">
   */
  message?: string;
  onBack?: () => void;
  onClose?: () => void;
  onRequestDocuments: (agreementId: string) => void;
  onSetTab: (index: number) => void;
}

const useStyles = makeStyles((theme: any) => ({
  grow: {
    flexGrow: 1
  },
  tableCell: {
    border: 'none',
    padding: theme.spacing(0.75, 0.5),
    verticalAlign: 'top',
    ...theme.typography.body2
  },
  tabsRoot: {
    minHeight: 36
  },
  tab: {
    minHeight: 36,
    minWidth: 'auto',
    textTransform: 'none',
    ...theme.typography.body2
  },
  tabIndicator: {
    display: 'none'
  },
  active: {
    color: theme.palette.active.dark
  },
  cancelled: {
    color: theme.palette.cancelled.dark
  },
  proposed: {
    color: theme.palette.proposed.dark
  }
}));

export const IdentifyAgreement: React.FC<Props> = ({
  agreement: {
    id,
    agreementDate,
    companyName,
    facility,
    fileNumber,
    initialAgreementCovers,
    name,
    status,
    url,
    notes,
    pipelineDiameter,
    pipelineProduct,
    pipelineLength,
    fetchedDocuments,
    hasFetched,
    isLoading
  },
  message,
  tabIndex,
  onBack,
  onClose,
  onRequestDocuments,
  onSetTab
}) => {
  const classes = useStyles();

  function onTabChange(_, newTab: number) {
    onSetTab(newTab);
    if (newTab === 1 && !isLoading && !hasFetched) {
      onRequestDocuments(id);
    }
  }

  const Title = (
    <Box component="div" display="flex" alignItems="center" p={1.5} paddingBottom={0.5}>
      {onBack && (
        <Box pr={1}>
          <IconButton onClick={onBack} aria-label="back button" size="small">
            <ArrowBackIcon />
          </IconButton>
        </Box>
      )}
      <Typography
        className={classes.grow}
        align="center"
        component="h4"
        variant="subtitle2"
        noWrap={true}
      >
        {name}
      </Typography>
      {onClose && (
        <Box pl={1}>
          <IconButton onClick={onClose} aria-label="close button" size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      )}
    </Box>
  );

  const Message = (msg: string) => (
    <Typography align="center" component="p" variant="caption" noWrap={true}>
      {msg}
    </Typography>
  );

  const CustomTabs = (
    <Tabs
      value={tabIndex}
      onChange={onTabChange}
      textColor="primary"
      aria-label="Popup tabs"
      classes={{ root: classes.tabsRoot, indicator: classes.tabIndicator }}
    >
      <Tab label="Fields" className={classes.tab} />
      <Tab label="Files" className={classes.tab} />
    </Tabs>
  );

  const InfoTableRow = (info: string, displayName: string) => {
    if (!(info && info.length)) {
      return;
    }
    return (
      <TableRow key={info}>
        <TableCell className={classes.tableCell}>{displayName}:</TableCell>
        <TableCell className={classes.tableCell} align="right">
          {info}
        </TableCell>
      </TableRow>
    );
  };

  const StatusTableRow = (agreementStatus: string) => {
    if (!(agreementStatus && agreementStatus.length)) {
      return;
    }
    return (
      <TableRow key={agreementStatus}>
        <TableCell className={classes.tableCell}>Status:</TableCell>
        <TableCell
          align="right"
          className={clsx({
            [classes.tableCell]: true,
            [classes.active]: agreementStatus === 'Active',
            [classes.cancelled]: agreementStatus === 'Cancelled',
            [classes.proposed]: agreementStatus === 'Proposed'
          })}
        >
          {agreementStatus}
        </TableCell>
      </TableRow>
    );
  };

  const LinkTableRow = (agreementId: string, urlLink: string, displayName: string) => {
    if (!(urlLink && urlLink.length)) {
      return;
    }
    return (
      <TableRow key={agreementId}>
        <TableCell className={classes.tableCell} colSpan={2}>
          <Link href={urlLink} target="_blank" rel="noopener noreferrer" underline="hover">
            <Box component="span" display="flex" flexDirection="row" alignItems="flex-start">
              <Box component="span" display="flex" pr={1}>
                <OpenInNewIcon fontSize="small" />
              </Box>
              <Typography component="span">{displayName}</Typography>
            </Box>
          </Link>
        </TableCell>
      </TableRow>
    );
  };

  const Content = (
    <Box overflow="auto" p={1}>
      <Table>
        {tabIndex === 0 && (
          <TableBody>
            {InfoTableRow(companyName, 'Company Name')}
            {InfoTableRow(facility, 'Facility')}
            {InfoTableRow(fileNumber, 'File Number')}
            {InfoTableRow(agreementDate, 'Agreement Date')}
            {StatusTableRow(status)}
            {InfoTableRow(initialAgreementCovers, 'Initial Agreement Covers')}
            {InfoTableRow(notes, 'Notes')}
            {InfoTableRow(pipelineDiameter, 'Pipeline Diameter')}
            {InfoTableRow(pipelineProduct, 'Pipeline Product')}
            {InfoTableRow(pipelineLength, 'Pipeline Length')}
            {LinkTableRow(url, url, 'Open Folder in Laserfiche')}
          </TableBody>
        )}

        {tabIndex === 1 && isLoading && (
          <TableBody>
            <TableRow>
              <TableCell className={classes.tableCell} align="center">
                <LinearProgress />
                <Box pt={1}>Loading Files</Box>
              </TableCell>
            </TableRow>
          </TableBody>
        )}

        {tabIndex === 1 && hasFetched === false && isLoading === false && (
          <TableBody>
            <TableRow>
              <TableCell className={classes.tableCell} align="center">
                <Box pb={1}>Unable to Load Files</Box>
                <Button variant="text" color="primary" onClick={onRequestDocuments.bind(null, id)}>
                  Retry
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        )}

        {tabIndex === 1 &&
          hasFetched &&
          fetchedDocuments &&
          (fetchedDocuments.length ? (
            <TableBody>
              {fetchedDocuments.map(document =>
                LinkTableRow(document.id, document.url, document.name)
              )}
            </TableBody>
          ) : (
            <Box pt={2}>No files in this folder</Box>
          ))}
      </Table>
    </Box>
  );

  return (
    <Box display="flex" flexDirection="column" overflow="hidden" bgcolor="background.paper">
      {message && (
        <Box flex="0 0 auto" py={1} bgcolor="error.light" color="error.contrastText">
          {Message(message)}
        </Box>
      )}
      {Title}
      {CustomTabs}
      <Divider />
      {Content}
    </Box>
  );
};

export default IdentifyAgreement;
